/**
 * LearnAgentActions — the doors into the learn agent's actual capabilities.
 *
 * Without this the board is a dashboard with a blank text box beside it: the
 * two things this agent genuinely does that nothing else on the surface can —
 * set you a target on a skill against a real exam date (`set_target` with
 * `exam_date`) and generate NEW practice grounded in the platform's knowledge
 * base (`create_quiz_from_knowledge {private: true}`) — were reachable only by
 * guessing the right sentence. A nurse at the bedside does not guess
 * sentences.
 *
 * These are not fake buttons. Each one composes a real, well-formed request in
 * the user's language and sends it as a turn; the agent runs its own tools
 * server-side and the board re-reads when the turn ends. The exam action
 * collects the two things the agent cannot invent — the SKILL and the date —
 * because a target names exactly one skill and `set_target` must never be
 * called with a made-up exam_date.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CalendarPlus, Sparkles } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { todayCivilDate } from '@frontend/utils/dates'

export interface LearnAgentActionsProps {
  /** Send a composed request as a turn. Mints the session when session-less. */
  onAsk: (message: string) => void
  /** A turn is in flight — don't let a second one stack. */
  disabled?: boolean
  className?: string
}

type OpenForm = 'exam' | 'practice' | null

export function LearnAgentActions({
  onAsk,
  disabled,
  className,
}: LearnAgentActionsProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState<OpenForm>(null)
  const [topic, setTopic] = useState('')
  const [examDate, setExamDate] = useState('')
  // Recomputed per render rather than pinned at mount: a board left open
  // across midnight would otherwise keep yesterday's floor.
  const today = todayCivilDate()
  // A date already in the past is a typo, never an intent. It would ride into
  // set_target's exam_date and silently corrupt the target it was meant to
  // represent.
  const examDateValid = !!examDate && examDate >= today

  const close = () => {
    setOpen(null)
    setTopic('')
    setExamDate('')
  }

  // Toggling a chip is the THIRD way out of a form, alongside submit and
  // Cancel, and the fields are shared between the two forms — so it has to
  // clear them like the other two do. It did not: a topic typed under "exam"
  // reappeared prefilled under "practice", and an exam date typed and
  // abandoned survived to ride into the NEXT exam request the learner did
  // submit. A stale exam_date there is a corrupted target, not a cosmetic
  // leftover.
  const toggleForm = (form: Exclude<OpenForm, null>) => {
    if (open === form) {
      close()
      return
    }
    setTopic('')
    setExamDate('')
    setOpen(form)
  }

  const submitExam = () => {
    if (!topic.trim() || !examDateValid) return
    onAsk(
      t('learn.actions.exam.request', {
        topic: topic.trim(),
        date: examDate,
        defaultValue:
          'I have an exam on {{topic}} on {{date}}. Set me a target on the {{topic}} skill with that exam date, and create private practice material for it.',
      }),
    )
    close()
  }

  const submitPractice = () => {
    if (!topic.trim()) return
    onAsk(
      t('learn.actions.practice.request', {
        topic: topic.trim(),
        defaultValue: 'Create a private practice quiz on {{topic}}, grounded in our guidelines.',
      }),
    )
    close()
  }

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex flex-wrap gap-2">
        <ActionChip
          icon={<CalendarPlus className="h-3.5 w-3.5" aria-hidden="true" />}
          label={t('learn.actions.exam.label')}
          active={open === 'exam'}
          disabled={disabled}
          onClick={() => toggleForm('exam')}
        />
        <ActionChip
          icon={<Sparkles className="h-3.5 w-3.5" aria-hidden="true" />}
          label={t('learn.actions.practice.label')}
          active={open === 'practice'}
          disabled={disabled}
          onClick={() => toggleForm('practice')}
        />
      </div>

      {open === 'exam' && (
        <ActionForm
          onSubmit={submitExam}
          onCancel={close}
          submitLabel={t('learn.actions.exam.submit')}
          canSubmit={!!topic.trim() && examDateValid}
          disabled={disabled}
        >
          <Input
            autoFocus
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder={t('learn.actions.topicPlaceholder')}
            aria-label={t('learn.actions.topicLabel')}
          />
          <Input
            type="date"
            value={examDate}
            min={today}
            onChange={(e) => setExamDate(e.target.value)}
            aria-label={t('learn.actions.exam.dateLabel')}
          />
        </ActionForm>
      )}

      {open === 'practice' && (
        <ActionForm
          onSubmit={submitPractice}
          onCancel={close}
          submitLabel={t('learn.actions.practice.submit')}
          canSubmit={!!topic.trim()}
          disabled={disabled}
        >
          <Input
            autoFocus
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder={t('learn.actions.topicPlaceholder')}
            aria-label={t('learn.actions.topicLabel')}
          />
        </ActionForm>
      )}
    </div>
  )
}

function ActionChip({
  icon,
  label,
  onClick,
  active,
  disabled,
}: {
  icon?: React.ReactNode
  label: string
  onClick: () => void
  active?: boolean
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      className={cn(
        'inline-flex min-h-9 items-center gap-1.5 rounded-full border px-3 text-[13px] font-semibold transition active:scale-95 disabled:opacity-50',
        active
          ? 'border-primary-200 bg-primary-50 text-primary-700'
          : 'border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50',
      )}
    >
      {icon}
      {label}
    </button>
  )
}

/** Enter submits, Escape cancels — one-handed at the bedside. */
function ActionForm({
  children,
  onSubmit,
  onCancel,
  submitLabel,
  canSubmit,
  disabled,
}: {
  children: React.ReactNode
  onSubmit: () => void
  onCancel: () => void
  submitLabel: string
  canSubmit: boolean
  disabled?: boolean
}) {
  const { t } = useTranslation()
  return (
    <form
      className="flex flex-col gap-2 rounded-[14px] border border-neutral-200 bg-white p-3 sm:flex-row sm:items-center"
      onSubmit={(e) => {
        e.preventDefault()
        onSubmit()
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onCancel()
      }}
    >
      {children}
      <div className="flex shrink-0 gap-2">
        <Button type="submit" size="pill" disabled={!canSubmit || disabled} className="min-h-11">
          {submitLabel}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="pill"
          onClick={onCancel}
          className="min-h-11"
        >
          {t('actions.cancel', 'Cancel')}
        </Button>
      </div>
    </form>
  )
}
